import { observer } from "mobx-react-lite";
import { useField, useForm } from "react-final-form";

import { Stack } from "@bps/fluent-ui";
import { FormsChannelType } from "@libs/gateways/forms/FormsGateway.dtos.ts";
import { CommunicationType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { Labels } from "@modules/practice/screens/shared-components/types/labels.enums.types.ts";
import { CommunicationFieldValue } from "@shared-types/practice/communication-field-value.type.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { ContactFetcher } from "@ui-components/ContactFetcher.tsx";
import { CheckboxField } from "@ui-components/form/CheckboxField.tsx";
import { DropdownField } from "@ui-components/form/DropdownField.tsx";
import { FieldSpy } from "@ui-components/form/FieldSpy.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";

import { PatientEditFormValues as PatientFormValues } from "../../shared-components/edit/PatientEditFormValues.tsx";
import { patientFormNameOf } from "./PatientEditFormValues.tsx";

interface SendFormDialogProps {
  onDismiss: () => void;
  value: CommunicationFieldValue;
}

interface SendFormValues {
  option: string;
  checkedForForms?: boolean;
}

export const SendFormDialog: React.FC<SendFormDialogProps> = observer(
  ({
    onDismiss,

    value
  }) => {
    const root = useStores();
    const {
      practice: {
        ui: { currentContactId }
      }
    } = root;

    const { change } = useForm<PatientFormValues>();

    const {
      input: { value: comms }
    } = useField<CommunicationFieldValue[]>(
      patientFormNameOf("communications"),
      {
        subscription: { value: true }
      }
    );

    const emailOptions = comms.length
      ? comms?.filter(x => x.type === CommunicationType.Email)
      : [];

    const emailIndex = emailOptions.findIndex(x => value.value === x.value);

    const smsOptions = comms.length
      ? comms?.filter(x => x.type === CommunicationType.Mobile)
      : [];

    const smsIndex = smsOptions.findIndex(x => value.value === x.value);

    const onCheckBoxChanged = (checked: boolean) => {
      if (value.type === CommunicationType.Email) {
        if (checked) {
          change(patientFormNameOf("emailForm"), emailIndex);
        } else {
          change(patientFormNameOf("emailForm"), undefined);
        }
      } else {
        // SMS
        if (checked) {
          change(patientFormNameOf("smsForm"), smsIndex);
        } else {
          change(patientFormNameOf("smsForm"), undefined);
        }
      }
    };

    const getChannelType = (type: CommunicationType | undefined) => {
      switch (type) {
        case CommunicationType.Email:
          return FormsChannelType.Email;

        case CommunicationType.Mobile:
          return FormsChannelType.SMS;

        default:
          return;
      }
    };
    if (!currentContactId) return null;

    return (
      <ContactFetcher contactId={currentContactId}>
        {patient => (
          <SubmissionFormDialog<SendFormValues>
            dialogName="sendForm"
            onSubmit={() => {
              change(patientFormNameOf("tempFormAddressValue"), value.value);
              change(
                patientFormNameOf("tempFormChannelType"),
                getChannelType(value.type)
              );
            }}
            onSubmitSucceeded={() => {
              onDismiss();
            }}
            initialValues={{ option: "personalDetails" }}
            dialogProps={{
              minWidth: 430,
              maxWidth: 430,
              onDismiss,
              dialogContentProps: {
                title: `Send form to ${patient.name}`
              }
            }}
            buttonsProps={{
              disableSubmitOnPristine: false,
              submitButtonProps: {
                text: "Save",
                iconProps: {
                  hidden: true
                }
              },
              cancelButtonProps: {
                onClick: onDismiss
              }
            }}
          >
            {() => (
              <Stack tokens={{ childrenGap: 16 }}>
                <DropdownField
                  styles={{ root: { width: "100%" } }}
                  name="option"
                  required
                  label="Form"
                  withNoEmptyOption
                  options={[
                    {
                      key: "personalDetails",
                      text: "Personal details"
                    }
                  ]}
                  validateOnInitialize
                />
                <CheckboxField
                  name="checkedForForms"
                  label={
                    value.type === CommunicationType.Email
                      ? Labels.emailFormLabel
                      : Labels.smsFormLabel
                  }
                  automationAttribute={`select-email-form-${emailIndex}`}
                />
                <FieldSpy name="checkedForForms" onChange={onCheckBoxChanged} />
              </Stack>
            )}
          </SubmissionFormDialog>
        )}
      </ContactFetcher>
    );
  }
);
